// Membership wizard step — tier / member number / benefits / self-enroll
// (SLICE-MEMBERSHIP-PASS).
//
// Edits the whole MembershipConfig (type_config). Unlike loyalty's structural
// "program" step there are no stamps or milestones — a membership pass carries
// a tier, a member number, and a back-of-pass benefits list. The whole config
// is patched on every change (the API expects the WHOLE MembershipConfig).
//
// These are STRUCTURAL-ish but membership doesn't lock type_config on activate
// (the brief locks only onboarding_fields + expiration) — so there's no
// readOnly variant here; the wizard simply omits this step once active
// (cosmetic-only mode shows the front + back steps).

import {
  DEFAULT_MEMBER_NUMBER_LABEL,
  MEMBERSHIP_BENEFIT_MAX_LENGTH,
  MEMBERSHIP_MAX_BENEFITS,
  MEMBERSHIP_MAX_TIERS,
  MEMBERSHIP_TIER_MAX_LENGTH,
  type MembershipConfig,
} from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'

// A safe default config to merge into — the draft always carries a non-null
// type_config for a membership program, but this keeps the patch helper total.
const EMPTY_MEMBERSHIP_CONFIG: MembershipConfig = {
  tiers_enabled: false,
  tiers: [],
  benefits: [],
  member_number_label: DEFAULT_MEMBER_NUMBER_LABEL,
  self_enroll_enabled: false,
}

export function StepMembership({ builder }: { builder: BuilderController }) {
  // Merge over the empty default so a program whose stored type_config predates
  // the tiers redesign (no `tiers` / `tiers_enabled` keys) still reads totally —
  // otherwise `config.tiers.length` throws. The owner's first save migrates the
  // row to the full shape.
  const config: MembershipConfig = {
    ...EMPTY_MEMBERSHIP_CONFIG,
    ...((builder.draft.type_config as MembershipConfig | null) ?? {}),
  }

  // Patch the WHOLE config — the API expects the complete MembershipConfig.
  function patch(next: Partial<MembershipConfig>) {
    builder.update({ type_config: { ...config, ...next } })
  }

  const benefits = config.benefits
  const canAddBenefit = benefits.length < MEMBERSHIP_MAX_BENEFITS

  function setBenefits(next: string[]) {
    patch({ benefits: next })
  }
  function updateBenefit(index: number, value: string) {
    setBenefits(benefits.map((b, i) => (i === index ? value : b)))
  }
  function removeBenefit(index: number) {
    setBenefits(benefits.filter((_, i) => i !== index))
  }
  function addBenefit() {
    if (!canAddBenefit) return
    setBenefits([...benefits, ''])
  }

  // Tiers — a switch (off by default). On → the owner lists the selectable
  // tier options; one is chosen per member at enrollment. Turning the switch
  // on seeds one empty row so the list editor is immediately usable.
  const tiers = config.tiers
  const canAddTier = tiers.length < MEMBERSHIP_MAX_TIERS
  // Surface the activation rules inline (tiers-on needs ≥1 named, unique tier)
  // so the owner fixes them here instead of bouncing off an activation gap.
  const namedTiers = tiers.map((t) => t.trim()).filter(Boolean)
  const hasNamedTier = namedTiers.length > 0
  const hasDuplicateTiers =
    new Set(namedTiers.map((t) => t.toLowerCase())).size < namedTiers.length
  function setTiers(next: string[]) {
    patch({ tiers: next })
  }
  function toggleTiers(enabled: boolean) {
    patch({ tiers_enabled: enabled, tiers: enabled && tiers.length === 0 ? [''] : tiers })
  }
  function updateTier(index: number, value: string) {
    setTiers(tiers.map((t, i) => (i === index ? value : t)))
  }
  function removeTier(index: number) {
    setTiers(tiers.filter((_, i) => i !== index))
  }
  function addTier() {
    if (!canAddTier) return
    setTiers([...tiers, ''])
  }

  return (
    <div className="program-step">
      <h2>Set up the membership</h2>
      <p className="program-step-sub">
        A membership pass shows a member number and the perks members get. No
        stamps to collect — members keep the pass for as long as they're a
        member. Turn on tiers if you offer more than one level.
      </p>

      <div className="program-field-block">
        <span className="program-field-label">Membership tiers</span>
        <label className="program-toggle">
          <input
            type="checkbox"
            checked={config.tiers_enabled}
            onChange={(e) => toggleTiers(e.target.checked)}
          />
          <span className="program-field-label">Use membership tiers</span>
        </label>
        <span className="program-field-hint">
          Off by default — a single-level membership shows no tier. Turn on to
          offer levels like "Gold", "Silver", "VIP". You (or the member, if
          self-enroll is on) pick the tier when the pass is issued.
        </span>
        {config.tiers_enabled && (
          <>
            <div className="program-benefit-list">
              {tiers.map((tier, i) => (
                <div className="program-benefit-row" key={i}>
                  <input
                    type="text"
                    className="owner-input"
                    value={tier}
                    maxLength={MEMBERSHIP_TIER_MAX_LENGTH}
                    placeholder="e.g. Gold"
                    onChange={(e) => updateTier(i, e.target.value)}
                    aria-label={`Tier ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="program-link-btn program-danger"
                    onClick={() => removeTier(i)}
                    aria-label={`Remove tier ${i + 1}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            {canAddTier && (
              <button
                type="button"
                className="owner-btn program-add-btn"
                onClick={addTier}
              >
                + Add tier
              </button>
            )}
            {!hasNamedTier && (
              <span className="program-field-hint">
                Add at least one tier name (e.g. "Gold") — a tiered membership
                needs one to show on the pass and to activate.
              </span>
            )}
            {hasDuplicateTiers && (
              <p className="program-contrast-warning" role="status">
                Two tiers have the same name. Give each tier a unique name.
              </p>
            )}
          </>
        )}
      </div>

      <div className="program-field-block">
        <label
          className="program-field-label"
          htmlFor="membership-number-label"
        >
          Member number label
        </label>
        <span className="program-field-hint">
          What you call the member's ID on the pass. Default is "Member No.".
        </span>
        <input
          id="membership-number-label"
          type="text"
          className="owner-input"
          value={config.member_number_label}
          maxLength={MEMBERSHIP_TIER_MAX_LENGTH}
          placeholder={DEFAULT_MEMBER_NUMBER_LABEL}
          onChange={(e) => patch({ member_number_label: e.target.value })}
        />
      </div>

      <div className="program-field-block">
        <span className="program-field-label">Member benefits</span>
        <span className="program-field-hint">
          The perks members get, shown on the back of the pass. Up to{' '}
          {MEMBERSHIP_MAX_BENEFITS}, one per line.
        </span>
        <div className="program-benefit-list">
          {benefits.map((benefit, i) => (
            <div className="program-benefit-row" key={i}>
              <input
                type="text"
                className="owner-input"
                value={benefit}
                maxLength={MEMBERSHIP_BENEFIT_MAX_LENGTH}
                placeholder="e.g. 10% off every visit"
                onChange={(e) => updateBenefit(i, e.target.value)}
                aria-label={`Benefit ${i + 1}`}
              />
              <button
                type="button"
                className="program-link-btn program-danger"
                onClick={() => removeBenefit(i)}
                aria-label={`Remove benefit ${i + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {canAddBenefit && (
          <button
            type="button"
            className="owner-btn program-add-btn"
            onClick={addBenefit}
          >
            + Add benefit
          </button>
        )}
      </div>

      <div className="program-field-block">
        <span className="program-field-label">How members join</span>
        <label className="program-toggle">
          <input
            type="checkbox"
            checked={config.self_enroll_enabled}
            onChange={(e) => patch({ self_enroll_enabled: e.target.checked })}
          />
          <span className="program-field-label">Let anyone self-enroll</span>
        </label>
        <span className="program-field-hint">
          Members are issued in store by you/your staff. Turn on to let anyone
          self-enroll via the poster QR.
        </span>
      </div>
    </div>
  )
}
